import { useEffect, useState } from 'react';
import {
  collection, onSnapshot, updateDoc, doc, addDoc, serverTimestamp, getDoc,
  runTransaction, increment                                        // ← NEW
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Link } from 'react-router-dom';

// ── NEW: Referral commission constants ────────────────────────────────────────
const COMMISSION_RATE = 0.05; // 5%

// ── NEW: Process referral commission (web/admin version) ──────────────────────
const processReferralCommission = async (depositedUserId, depositAmount) => {
  try {
    // Get the depositing user's data
    const refereeSnap = await getDoc(doc(db, 'users', depositedUserId));
    if (!refereeSnap.exists()) return;

    const refereeData = refereeSnap.data();
    const referrerId  = refereeData?.referredByUid;

    // No referral link — skip silently
    if (!referrerId) return;

    const commission = Math.floor(depositAmount * COMMISSION_RATE);
    if (commission <= 0) return;

    // Atomic transaction: add commission to referrer balance
    await runTransaction(db, async (transaction) => {
      const referrerRef  = doc(db, 'users', referrerId);
      const referrerSnap = await transaction.get(referrerRef);
      if (!referrerSnap.exists()) throw new Error('Referrer not found');

      transaction.update(referrerRef, {
        balance:          increment(commission),
        referralEarnings: increment(commission),
      });
    });

    // Log referral earning record
    await addDoc(collection(db, 'referralEarnings'), {
      referrerId:       referrerId,
      refereeId:        depositedUserId,
      refereeName:      refereeData?.username ?? 'Unknown',
      depositAmount:    depositAmount,
      commissionAmount: commission,
      commissionRate:   COMMISSION_RATE * 100,
      createdAt:        serverTimestamp(),
    });

    console.log(`✅ Referral commission ৳${commission} credited to ${referrerId}`);
    return commission;

  } catch (err) {
    // Commission failure should NOT block the deposit approval
    console.error('Referral commission error (non-critical):', err);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
export default function Deposits() {
  const [deposits, setDeposits] = useState([]);
  const [filter, setFilter]     = useState('pending');
  const [loading, setLoading]   = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'deposits'), (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
      setDeposits(data);
    });
    return () => unsub();
  }, []);

  // ── handleApprove — referral commission যোগ হয়েছে ────────────────────────
  const handleApprove = async (dep) => {
    if (!window.confirm(`৳${dep.amount} deposit approve করবে? User এর wallet এ balance যাবে।`)) return;
    setLoading(dep.id);
    try {
      // Get current balance
      const userRef      = doc(db, 'users', dep.userId);
      const userSnap     = await getDoc(userRef);
      const currentBalance = userSnap.data()?.balance ?? 0;
      const newBalance   = currentBalance + dep.amount;

      // Update user balance
      await updateDoc(userRef, { balance: newBalance });

      // Update deposit status
      await updateDoc(doc(db, 'deposits', dep.id), {
        status:     'approved',
        approvedAt: serverTimestamp(),
      });

      // Save wallet history
      await addDoc(collection(db, 'walletHistory'), {
        userId:       dep.userId,
        type:         'deposit',
        amount:       dep.amount,
        note:         `Deposit approved via ${dep.method} (TrxID: ${dep.trxId})`,
        balanceAfter: newBalance,
        createdAt:    serverTimestamp(),
      });

      // ── NEW: Process referral commission ──────────────────────────────────
      const commission = await processReferralCommission(dep.userId, dep.amount);
      const commMsg    = commission ? `\n🔗 Referral commission ৳${commission} credited!` : '';

      alert(`✅ ৳${dep.amount} user এর wallet এ add হয়েছে!${commMsg}`);
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setLoading(null);
  };

  // ── handleReject — unchanged ──────────────────────────────────────────────
  const handleReject = async (dep) => {
    if (!window.confirm(`এই deposit reject করবে?`)) return;
    setLoading(dep.id);
    try {
      await updateDoc(doc(db, 'deposits', dep.id), {
        status:     'rejected',
        rejectedAt: serverTimestamp(),
      });

      await addDoc(collection(db, 'walletHistory'), {
        userId:       dep.userId,
        type:         'deposit_rejected',
        amount:       dep.amount,
        note:         `Deposit rejected via ${dep.method} (TrxID: ${dep.trxId})`,
        balanceAfter: null,
        createdAt:    serverTimestamp(),
      });

      alert('❌ Deposit rejected।');
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setLoading(null);
  };

  const filtered = deposits.filter(d => {
    if (filter === 'all') return true;
    return d.status === filter;
  });

  const totalPending  = deposits.filter(d => d.status === 'pending').reduce((s, d) => s + (d.amount ?? 0), 0);
  const totalApproved = deposits.filter(d => d.status === 'approved').reduce((s, d) => s + (d.amount ?? 0), 0);

  const sidebarLinks = [
    { to: '/',                label: '🏠 Dashboard'       },
    { to: '/matches',         label: '🎯 Matches'          },
    { to: '/results',         label: '🏆 Results'          },
    { to: '/users',           label: '👥 Users'            },
    { to: '/deposits',        label: '💰 Deposits',  active: true },
    { to: '/withdrawals',     label: '⬆️ Withdrawals'     },
    { to: '/settings',        label: '⚙️ Settings'         },
    { to: '/password-resets', label: '🔑 Password Resets' },
    { to: '/videos',          label: '🎬 Videos'           },
  ];

  return (
    <div style={styles.container}>

      {/* Sidebar */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarLogo}>🎮 FF Arena</div>
        <div style={styles.sidebarSub}>Admin Panel</div>
        <nav style={styles.nav}>
          {sidebarLinks.map(l => (
            <Link key={l.to} to={l.to}
              style={l.active ? styles.navItemActive : styles.navItem}>
              {l.label}
            </Link>
          ))}
        </nav>
      </div>

      {/* Main */}
      <div style={styles.main}>
        <div style={styles.topRow}>
          <div>
            <h1 style={styles.pageTitle}>💰 Deposits</h1>
            <p style={styles.pageSub}>User দের deposit request approve বা reject করো</p>
          </div>
        </div>

        {/* ── NEW: Referral Commission Info Banner ── */}
        <div style={styles.commissionBanner}>
          <span style={{ fontSize: 18 }}>🔗</span>
          <div>
            <div style={{ color: '#f0c040', fontWeight: 'bold', fontSize: 14 }}>
              Auto Referral Commission Active
            </div>
            <div style={{ color: '#888', fontSize: 12, marginTop: 2 }}>
              Deposit approve করলে referrer এর account এ automatically {COMMISSION_RATE * 100}% commission যাবে
            </div>
          </div>
        </div>

        {/* Stats */}
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <div style={{ color: '#f4c430', fontSize: 28, fontWeight: 'bold', marginBottom: 4 }}>
              {deposits.filter(d => d.status === 'pending').length}
            </div>
            <div style={styles.statLabel}>Pending Requests</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ color: '#ff6b35', fontSize: 28, fontWeight: 'bold', marginBottom: 4 }}>
              ৳{totalPending}
            </div>
            <div style={styles.statLabel}>Pending Amount</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ color: '#00c896', fontSize: 28, fontWeight: 'bold', marginBottom: 4 }}>
              ৳{totalApproved}
            </div>
            <div style={styles.statLabel}>Total Approved</div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div style={styles.filterRow}>
          {[
            { key: 'pending',  label: '⏳ Pending'  },
            { key: 'approved', label: '✅ Approved' },
            { key: 'rejected', label: '❌ Rejected' },
            { key: 'all',      label: '📋 All'      },
          ].map(f => (
            <button key={f.key}
              style={filter === f.key ? styles.filterActive : styles.filterBtn}
              onClick={() => setFilter(f.key)}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div style={styles.tableCard}>
          <table style={styles.table}>
            <thead>
              <tr>
                {['User ID', 'Method', 'TrxID', 'Amount', 'Referral', 'Time', 'Status', 'Actions'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ ...styles.td, textAlign: 'center', color: '#555', padding: 40 }}>
                    কোনো deposit নেই
                  </td>
                </tr>
              ) : filtered.map(d => (
                <DepositRow
                  key={d.id}
                  d={d}
                  loading={loading}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  commissionRate={COMMISSION_RATE}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── NEW: DepositRow — shows referral status per deposit ───────────────────────
function DepositRow({ d, loading, onApprove, onReject, commissionRate }) {
  const [referrerName, setReferrerName] = useState(null);
  const [checked, setChecked]           = useState(false);

  useEffect(() => {
    // Fetch referrer info to show in table
    const fetchReferrer = async () => {
      try {
        const userSnap = await getDoc(doc(db, 'users', d.userId));
        if (!userSnap.exists()) return;
        const referrerId = userSnap.data()?.referredByUid;
        if (!referrerId) { setChecked(true); return; }
        const referrerSnap = await getDoc(doc(db, 'users', referrerId));
        if (referrerSnap.exists()) {
          setReferrerName(referrerSnap.data()?.username ?? 'Unknown');
        }
      } catch (e) {}
      setChecked(true);
    };
    fetchReferrer();
  }, [d.userId]);

  const commission = Math.floor((d.amount ?? 0) * commissionRate);

  return (
    <tr key={d.id}>
      <td style={styles.td}>
        <span style={{ color: '#888', fontSize: 11 }}>{d.userId?.slice(0, 10)}...</span>
      </td>
      <td style={styles.td}>
        <span style={{
          background: d.method === 'bKash' ? '#e2136e22' : '#f4732a22',
          color: d.method === 'bKash' ? '#e2136e' : '#f4732a',
          padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 'bold',
        }}>{d.method}</span>
      </td>
      <td style={styles.td}>
        <span style={{
          color: '#f4c430', fontSize: 12, fontFamily: 'monospace',
          background: '#f4c43011', padding: '2px 8px', borderRadius: 6,
        }}>{d.trxId}</span>
      </td>
      <td style={styles.td}>
        <span style={{ color: '#00c896', fontWeight: 'bold', fontSize: 15 }}>
          ৳{d.amount}
        </span>
      </td>

      {/* ── NEW: Referral column ── */}
      <td style={styles.td}>
        {!checked ? (
          <span style={{ color: '#555', fontSize: 11 }}>...</span>
        ) : referrerName ? (
          <div>
            <div style={{ color: '#f0c040', fontSize: 11, fontWeight: 'bold' }}>
              🔗 @{referrerName}
            </div>
            <div style={{ color: '#00c896', fontSize: 11 }}>
              +৳{commission} commission
            </div>
          </div>
        ) : (
          <span style={{ color: '#444', fontSize: 11 }}>—</span>
        )}
      </td>

      <td style={styles.td}>
        <span style={{ color: '#666', fontSize: 12 }}>
          {d.createdAt?.seconds
            ? new Date(d.createdAt.seconds * 1000).toLocaleString()
            : '—'}
        </span>
      </td>
      <td style={styles.td}>
        <span style={{
          background: d.status === 'pending' ? '#f4c43022'
            : d.status === 'approved' ? '#00c89622' : '#ff444422',
          color: d.status === 'pending' ? '#f4c430'
            : d.status === 'approved' ? '#00c896' : '#ff4444',
          padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 'bold',
        }}>
          {d.status === 'pending' ? '⏳ Pending'
            : d.status === 'approved' ? '✅ Approved' : '❌ Rejected'}
        </span>
      </td>
      <td style={styles.td}>
        {d.status === 'pending' ? (
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              style={{ ...styles.approveBtn, opacity: loading === d.id ? 0.6 : 1 }}
              onClick={() => onApprove(d)}
              disabled={loading === d.id}
            >
              {loading === d.id ? '...' : '✅ Approve'}
            </button>
            <button
              style={{ ...styles.rejectBtn, opacity: loading === d.id ? 0.6 : 1 }}
              onClick={() => onReject(d)}
              disabled={loading === d.id}
            >
              ❌ Reject
            </button>
          </div>
        ) : (
          <span style={{ color: '#555', fontSize: 12 }}>
            {d.status === 'approved'
              ? `✅ ${new Date((d.approvedAt?.seconds ?? 0) * 1000).toLocaleString()}`
              : '❌ Rejected'}
          </span>
        )}
      </td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
const styles = {
  container: { display: 'flex', minHeight: '100vh', background: '#0a0a1a' },
  sidebar: {
    width: 240, background: '#111128', borderRight: '1px solid #1e1e3a',
    padding: '24px 0', display: 'flex', flexDirection: 'column',
    position: 'fixed', height: '100vh',
  },
  sidebarLogo: { color: '#f4c430', fontSize: 20, fontWeight: 'bold', padding: '0 24px', marginBottom: 4 },
  sidebarSub:  { color: '#555', fontSize: 12, padding: '0 24px', marginBottom: 32 },
  nav:         { display: 'flex', flexDirection: 'column', flex: 1 },
  navItem: { color: '#888', textDecoration: 'none', padding: '12px 24px', fontSize: 14, fontWeight: '500', borderLeft: '3px solid transparent' },
  navItemActive: { color: '#f4c430', textDecoration: 'none', padding: '12px 24px', fontSize: 14, fontWeight: '600', borderLeft: '3px solid #f4c430', background: '#f4c43011' },
  main:    { marginLeft: 240, flex: 1, padding: 32 },
  topRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  pageTitle: { color: '#fff', fontSize: 28, fontWeight: 'bold', margin: 0 },
  pageSub:   { color: '#666', fontSize: 14, marginTop: 6 },

  // ── NEW: Commission banner ─────────────────────────────────────────────────
  commissionBanner: {
    display: 'flex', alignItems: 'center', gap: 14,
    background: 'rgba(240,192,64,0.08)', border: '1px solid rgba(240,192,64,0.25)',
    borderRadius: 12, padding: '14px 20px', marginBottom: 24,
  },

  statsRow: { display: 'flex', gap: 16, marginBottom: 24 },
  statCard: {
    background: '#111128', borderRadius: 14, border: '1px solid #1e1e3a',
    padding: '20px 28px', minWidth: 180,
  },
  statLabel: { color: '#666', fontSize: 13 },
  filterRow: { display: 'flex', gap: 10, marginBottom: 20 },
  filterBtn: {
    background: '#111128', border: '1px solid #333', borderRadius: 10,
    color: '#888', fontSize: 13, fontWeight: '600', padding: '8px 18px', cursor: 'pointer',
  },
  filterActive: {
    background: '#f4c430', border: '1px solid #f4c430', borderRadius: 10,
    color: '#000', fontSize: 13, fontWeight: '600', padding: '8px 18px', cursor: 'pointer',
  },
  tableCard: { background: '#111128', borderRadius: 16, border: '1px solid #1e1e3a', overflow: 'auto' },
  table:     { width: '100%', borderCollapse: 'collapse' },
  th: { color: '#666', fontSize: 12, fontWeight: '600', padding: '14px 16px', textAlign: 'left', borderBottom: '1px solid #1e1e3a', whiteSpace: 'nowrap' },
  td: { color: '#ccc', fontSize: 13, padding: '14px 16px', borderBottom: '1px solid #1e1e3a11' },
  approveBtn: {
    background: '#00c89622', border: '1px solid #00c89644',
    color: '#00c896', borderRadius: 8, padding: '6px 12px',
    cursor: 'pointer', fontSize: 12, fontWeight: '600', whiteSpace: 'nowrap',
  },
  rejectBtn: {
    background: '#ff444422', border: '1px solid #ff444444',
    color: '#ff6b6b', borderRadius: 8, padding: '6px 12px',
    cursor: 'pointer', fontSize: 12, fontWeight: '600',
  },
};